<template>
	<TheStdLayout>

		<template #pageheader>
			<router-link :to="{name: 'goal-collections' }" class="text-accent hover:text-accent-focus">
				{{ $t('Grade-level goals') }}
			</router-link>
			<span v-if="true" class="inline-flex items-center">
				<ChevronRightIcon class="w-4 h-4 mx-2"/>
				{{ goalCollection?.name }}
			</span>
		</template>

		<div class="px-4 py-6 mx-auto sm:px-6 md:px-8">

			<FloatingPlusButton v-if="canImportGoals" :loading="goalLaunchPending" :pulsate="loaded && collectionGoalsStore.curriculumItems.length === 0 ? true : false" @push="addGoals" />

			<Skeleton v-if="!loaded" :withThumb="false" :items="5" class="mt-10" />

			<template v-else-if="collectionGoalsStore.curriculumItems.length">
				<GoalFilter
					:filter="collectionGoalsStore.filter"
					:initialFilter="collectionGoalsStore.getInitialFilter()"
					:goalSources="goalSources"
					:collapsable="true"
					:collapsed="false"
					@change="collectionGoalsStore.applyFilter"
					class="mb-8"
				/>

				<GoalList
					:store="collectionGoalsStore"
					:action="canEditGoals ? 'edit' : 'view'"
					@requestEdit="handleGoalEditRequest"
					@requestRemoval="handleGoalRemoveRequest"
				/>
			</template>

			<EmptyState v-else-if="canImportGoals"
				:title="$t('No goals added yet')"
				:description="$t('Start adding goals from the curriculum.')"
			/>

			<EmptyState v-else-if="usersStore.me?.hasAdminPermissions()"
				:title="$t('Goals are not available yet for this school account')"
				:description="$t('#instruction: curriculum not set')"
			/>

			<EmptyState v-else
				:title="$t('No goals available yet')"
				:description="$t('The account administrator has not added any goals yet')"
			/>

		</div>

		<!-- Currently no longer supported -->
		<GoalEditModal v-if="showGoalFormModal" :goal="goal" @requestClose="closeEditPopup" />

		<component
			v-if="activeGoalLaunch?.kind === 'external'"
			:is="activeGoalLaunch.component"
			context="gradeLevelGoals"
			:selectedGoals="collectionGoalsStore.items"
			@goalSelected="addGoal"
			@goalUnselected="removeGoal"
			@opened="goalLaunchPending = false"
			@requestUnmount="activeGoalLaunch = null; goalLaunchPending = false"
		/>

		<GoalSelector
			v-else-if="activeGoalLaunch?.kind === 'drawer'"
			:title="$t('Curriculum')"
			:curriculum="activeGoalLaunch.curriculum"
			:fixedSource="activeGoalLaunch.fixedSource"
			:selectedGoals="collectionGoalsStore.items"
			@goalSelected="addGoal"
			@goalUnselected="removeGoal"
			@requestUnmount="activeGoalLaunch = null"
		/>

	</TheStdLayout>
</template>

<script lang="ts">
import { mapStores } from "pinia";
import {useForkedGoalsStore} from "@/stores/Goals.store";
import {useUsersStore} from "@/stores/Users.store";
import { useGoalCollectionsStore } from "@/stores/GoalCollections.store";
import { GoalCollection } from "@/models/GoalCollection.model.ts";
import GoalFilter from '@/components/GoalFilter.vue';
import GoalList from "@/components/GoalList.vue";
import GoalEditModal from '@/components/GoalEditModal.vue';
import Skeleton from '@/components/ui/Skeleton.vue';
import EmptyState from "@/components/ui/EmptyState.vue";
import {ArrowLeftIcon, PlusIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';
import {useOrganisationsStore} from "@/stores/Organisations.store";
import {GoalFilterDef} from "@/stores/filters/GoalFilterDef.ts";
import {Goal} from "@/models/Goal.model.ts";
import {CollectionChangeBuffer} from "@/utils/CollectionChangeBuffer.util.ts";
import {GoalSource} from "@/utils/GoalSource.ts";
import GoalSelector from "@/components/GoalSelector.vue";
import { GoalPickerLaunchDescriptor, resolveGoalPickerLaunch } from "@/goals/utils/GoalPickerLaunch.util";


export default {

	data() {
		return {
			loaded: false,
			goal: null as Goal | null,
			showGoalFormModal: false,
			goalCollection: null as GoalCollection | null,
			updateBuffer: new CollectionChangeBuffer(useForkedGoalsStore('collectionGoals')(), 1000),
			activeGoalLaunch: null as GoalPickerLaunchDescriptor | null,
			// True from the click until the external picker reports `opened` (or unmounts).
			goalLaunchPending: false,
		};
	},

	components: {
		GoalSelector,
		ArrowLeftIcon,
		Skeleton,
		GoalFilter,
		GoalList,
		GoalEditModal,
		EmptyState,
		PlusIcon, ChevronRightIcon,
	},

	computed: {
		...mapStores(
			useForkedGoalsStore('collectionGoals'),
			useUsersStore,
			useOrganisationsStore,
			useGoalCollectionsStore,
		),

		canImportGoals(): boolean {
			return this.collectionGoalsStore.curriculum &&
				this.usersStore.me?.hasAdminPermissions() &&
				this.goalCollection?.editable;
		},

		canEditGoals(): boolean {
			return this.collectionGoalsStore.curriculum &&
				this.usersStore.me?.hasAdminPermissions() &&
				this.goalCollection?.editable;
		},

		goalSources(): GoalSource[] {
			return this.collectionGoalsStore.allSources;
		},
	},

	async mounted() {

		await this.load();
		this.loaded = true;

		this.$watch(
			() => this.$route.params,
			(newValue) => {
				this.loaded = false;
				this.load();
			}
		);
	},

	methods: {

		async load() {

			const organisation = this.organisationsStore.getCurrentOrganisation();
			const collectionId = this.$route.params.collectionId;

			await this.goalCollectionsStore.loadOnce();
			this.goalCollection = this.goalCollectionsStore.find(collectionId);

			// Collections are single-curriculum (spec 3.3). Legacy collections without
			// a curriculumId fall back to the school's current curriculum.
			const curriculum = this.goalCollection?.curriculum ?? organisation.curriculum;
			if (!curriculum) {
				return;
			}

			this.collectionGoalsStore.setCurriculum(curriculum, collectionId);

			const filter = new GoalFilterDef();
			await this.collectionGoalsStore.applyFilter(filter);

			this.loaded = true;

			// automatically open create popup
			try {
				if (window.history.state && window.history.state.create == '1') {
					this.addGoals();
				}
			} catch (e) {
				// ignore.
			}

		},

		handleGoalEditRequest(goal: Goal) {
			this.goal = goal;
			this.showGoalFormModal = true;
		},

		async handleGoalRemoveRequest(goal: Goal) {
			// Subgoals travel with their parent: deleting the parent takes them along.
			// (The list hides the trash icon on subgoal rows, so goal is never a subgoal here.)
			const subgoals = goal.isSubgoal()
				? []
				: (this.collectionGoalsStore.items as Goal[]).filter(
					(item) => item.isSubgoal()
						&& item.source === goal.source
						&& item.sourceParentPath === goal.fullPath
				);
			try {
				for (const subgoal of subgoals) {
					await this.collectionGoalsStore.delete(subgoal);
				}
				await this.collectionGoalsStore.delete(goal);
			} catch (e) {
				this.$notify({
					title: "Error!",
					text: '' + e,
					type: "error",
				});
			}
		},

		closeEditPopup() {
			this.goal = null;
			this.showGoalFormModal = false;
		},

		addGoals() {
			if (!this.canImportGoals) {
				return;
			}

			// One path for every curriculum: external selectors (ZiLL, OVSG, op.stap) mount
			// their picker directly, GO/SLO get the GoalSelector drawer. All of them emit
			// per-goal selected/unselected events into the collection change-buffer.
			const descriptor = resolveGoalPickerLaunch({ curriculum: this.collectionGoalsStore.curriculum });
			this.goalLaunchPending = descriptor.kind === 'external';
			this.activeGoalLaunch = descriptor;
		},

		addGoal(goal: Goal) {
			this.updateBuffer.addToCreateBuffer(goal);
		},

		removeGoal(goal: Goal) {
			this.updateBuffer.addToDeleteBuffer(goal);
		},

	},

};

</script>
